import { useState } from "react";

import { OrganizationAssistantDestination, OrganizationRoutes } from "@simplyk/common";
import { useRouter } from "next/navigation";

import type { DestinationTarget } from "./buildDestinationRoute";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { useChatWidgetRoute } from "@/components/ChatWidgetVisibilityManager/helpers/useChatWidgetRoute";
import { useAssistantConfig } from "@/features/Assistant/contexts/AssistantConfigContext";
import { useAssistantWidgetContext } from "@/features/Assistant/contexts/AssistantWidgetContext";

export type NavigationNotice = "identityProcessing" | "error";

type RouteTarget = Extract<DestinationTarget, { kind: "route" }>;

export interface StripeOnboarding {
  hasFinishedOnboarding: boolean;
  getRedirectionLink: () => Promise<string | null>;
}

export const useNavigationAction = (destination: OrganizationAssistantDestination | null) => {
  const router = useRouter();
  const buildUrl = useUrlBuilder();
  const widget = useAssistantWidgetContext();
  const { feature } = useAssistantConfig();
  const { pathWithoutLocale, routeType } = useChatWidgetRoute();
  const { logAmplitudeEvent } = useAmplitude();
  const { mutateAsync: createIdentityVerificationSession, isPending: isVerifyingIdentity } =
    trpc.createIdentityVerificationSession.useMutation();
  const [notice, setNotice] = useState<NavigationNotice | null>(null);

  const logClick = () =>
    logAmplitudeEvent(AmplitudeEvents.AssistantNavigationClicked, {
      feature,
      routeType,
      path: pathWithoutLocale,
      destination,
    });

  const leaveTo = (url: string) => {
    widget?.closePanel();
    window.location.href = url;
  };

  const openRoute = ({ route, hash }: Omit<RouteTarget, "kind">) => {
    logClick();
    widget?.closePanel();
    router.push(buildUrl({ route, hash, includeRouterSearchParams: false }));
  };

  const verifyIdentity = async () => {
    logClick();
    setNotice(null);
    const { data, error } = await createIdentityVerificationSession();
    if (error) {
      setNotice(error.code === "identity_verification_session_under_review" ? "identityProcessing" : "error");
      return;
    }
    if (!data?.url) {
      setNotice("error");
      return;
    }
    leaveTo(data.url);
  };

  const openStripeOnboarding = async ({ hasFinishedOnboarding, getRedirectionLink }: StripeOnboarding) => {
    if (!hasFinishedOnboarding) {
      openRoute({ route: OrganizationRoutes.StripeOnboarding });
      return;
    }
    logClick();
    const link = await getRedirectionLink();
    if (link) {
      leaveTo(link);
    }
  };

  return { openRoute, verifyIdentity, openStripeOnboarding, notice, isVerifyingIdentity };
};
